import type { MyUsageResponse } from "@softmax/observatory-client/types";

interface KindView {
  kind: string;
  label: string;
  amountUsd: number | null;
  detail: string;
}

interface CoworldView {
  key: string;
  coworld: string;
  llmEstimatedCostUsd: number;
  computeCostUsd: number;
  estimatedTotalCostUsd: number;
}

interface UsageView {
  kinds: KindView[];
  coworlds: CoworldView[];
  totalUsd: number;
  isEmpty: boolean;
}

const kindLabels: Record<string, string> = {
  llm: "LLM (OpenRouter)",
  llm_bedrock: "LLM (Bedrock)",
  ecs_compute: "Compute",
};

const llmKinds = new Set(["llm", "llm_bedrock"]);

function kindDetail({
  kind,
  invocations,
  inputTokens,
  outputTokens,
}: {
  kind: string;
  invocations: number;
  inputTokens: number;
  outputTokens: number;
}): string {
  if (llmKinds.has(kind)) {
    return `${invocations.toLocaleString("en-US")} calls · ${(inputTokens + outputTokens).toLocaleString("en-US")} tokens`;
  }

  return `${invocations.toLocaleString("en-US")} ${invocations === 1 ? "job" : "jobs"}`;
}

export function buildUsageView(response: MyUsageResponse): UsageView {
  const kinds: KindView[] = response.kinds.map((row) => ({
    kind: row.kind,
    label: kindLabels[row.kind] ?? row.kind,
    amountUsd: row.estimated_cost_usd ?? row.cost_usd ?? null,
    detail: kindDetail({
      kind: row.kind,
      invocations: row.invocations,
      inputTokens: row.input_tokens,
      outputTokens: row.output_tokens,
    }),
  }));
  // The API attributes costs to upload versions; this table summarizes named Coworlds.
  const byName = new Map<string, CoworldView>();

  for (const row of response.by_coworld) {
    const existing = byName.get(row.coworld);

    if (existing) {
      existing.llmEstimatedCostUsd += row.llm_estimated_cost_usd;
      existing.computeCostUsd += row.compute_cost_usd;
      existing.estimatedTotalCostUsd += row.estimated_total_cost_usd;
    } else {
      byName.set(row.coworld, {
        key: row.coworld,
        coworld: row.coworld,
        llmEstimatedCostUsd: row.llm_estimated_cost_usd,
        computeCostUsd: row.compute_cost_usd,
        estimatedTotalCostUsd: row.estimated_total_cost_usd,
      });
    }
  }

  const coworlds = [...byName.values()].toSorted(
    (a, b) =>
      b.estimatedTotalCostUsd - a.estimatedTotalCostUsd ||
      a.coworld.localeCompare(b.coworld, "en-US"),
  );

  return {
    kinds,
    coworlds,
    totalUsd: response.estimated_total_cost_usd,
    isEmpty: response.kinds.every(
      (row) =>
        row.invocations === 0 &&
        (row.estimated_cost_usd ?? row.cost_usd ?? 0) === 0,
    ),
  };
}
